import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import {
  Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  Grid, Chip, Avatar, Box, IconButton, Tooltip, MenuItem,
  Select, Alert, FormControl, InputLabel, Card, CardContent,
  Divider, Badge, Stack, Snackbar
} from '@mui/material';
import {
  Add, Email, TwoWheeler, Person,
  AccessTime, TimerOff, Autorenew, CheckCircle, 
  HighlightOff, DirectionsBike, EventAvailable,
  EventBusy, LibraryAddCheck, HowToReg, Edit, Delete, PictureAsPdf,
  ElectricBolt, Build
} from '@mui/icons-material';
import { green, orange, blue, red, purple, deepPurple, grey } from '@mui/material/colors';
import { getRiders, getScooters, createRental, getRentals, checkExpiringRentals, updateRental, deleteRental } from '../services/riderService';
import api from '../services/api';
import { useAuth } from '../services/auth/AuthContext';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';

// case 1: Let say a scooter return is overdue and date has passed but rider didnt returned it on the end time, in such case
//  admin or student employee has to first update the end date (date on which they return) and then change the status.
const Rental = () => {
  const CONSENT_PDF_FILENAME = 'Consent-not-req-signature-HRP-5011-4.pdf';
  const [riders, setRiders] = useState([]);
  const [scooters, setScooters] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRider, setCurrentRider] = useState({
    userId: "",
    fullName: "",
    email: "",
    studentId: "",
    scooterId: "",
    rentalDays: 14
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const handleCloseError = () => setError("");
  const handleCloseSuccess = () => setSuccess("");
  const [reminders, setReminders] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEmployee = user?.role === 'student employee';
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editRental, setEditRental] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('start_time');
  const [sortOrder, setSortOrder] = useState('desc');
  const [rentalDialogStep, setRentalDialogStep] = useState(0);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentSubmitted, setConsentSubmitted] = useState(false);
  const [consentSubmittedAt, setConsentSubmittedAt] = useState('');

  const statusOptions = [
    { value: 'rented', label: 'Rented', color: blue[500], icon: <ElectricBolt /> },
    { value: 'overdue', label: 'Overdue', color: red[500], icon: <HighlightOff /> },
    { value: 'returned', label: 'Returned', color: green[500], icon: <CheckCircle /> },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ridersList, scootersList, rentalsList] = await Promise.all([
          getRiders(),
          getScooters(),
          getRentals()
        ]);
        setRiders(ridersList);
        setScooters(scootersList);
        setRentals(rentalsList);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const checkExpiring = async () => {
      try {
        const result = await checkExpiringRentals();
        if (result.reminders && result.reminders.length > 0) {
          setReminders(result.reminders);
        }
      } catch (err) {
        console.error("Error checking expiring rentals:", err);
      }
    };

    checkExpiring();
    const interval = setInterval(checkExpiring, 3600000); // Check every hour
    return () => clearInterval(interval);
  }, []);

  const handleSelectRider = (uid) => {
    const rider = riders.find((u) => String(u.user_id) === String(uid));
    if (!rider) return;
    
    // Safely parse user_details and provide default values
    let userDetails = {};
    try {
      userDetails = JSON.parse(rider.user_details || "{}");
    } catch (e) {
      console.error("Error parsing user details:", e);
      userDetails = {};
    }

    setCurrentRider({
      userId: String(rider.user_id),
      fullName: userDetails.name || rider.name || "",
      email: rider.email || "",
      studentId: userDetails.student_id || rider.student_id || "",
      scooterId: "",
      rentalDays: 14
    });
    setConsentSubmitted(false);
    setConsentSubmittedAt('');
  };

  const handleCreateRental = async () => {
    if (!consentSubmitted) {
      setError("Submit consent form before creating rental.");
      return;
    }

    if (!currentRider.userId || !currentRider.scooterId) {
      setError("Select both rider and scooter.");
      return;
    }

    // Check if user already has an available rental
    const userAvailableRental = rentals.find(
      r => r.user_id === currentRider.userId && r.status === 'available'
    );

    if (userAvailableRental) {
      setError("This user already has an available rental. Please complete the current rental before creating a new one.");
      return;
    }

    try {
      // Format the date to match backend requirements
      const now = new Date();
      const endDate = new Date(now);
      endDate.setDate(now.getDate() + currentRider.rentalDays);
      
      const formattedStartDate = now.toISOString().split('.')[0]; // Remove milliseconds
      const formattedEndDate = endDate.toISOString().split('.')[0]; // Remove milliseconds
      
      const rentalData = {
        user_id: parseInt(currentRider.userId),
        scooter_id: parseInt(currentRider.scooterId),
        start_time: formattedStartDate,
        end_time: formattedEndDate,
        status: 'rented'
      };

      
      const response = await createRental(rentalData);
      
      // Refresh data
      const [freshScooters, freshRentals] = await Promise.all([
        getScooters(),
        getRentals()
      ]);
      setScooters(freshScooters);
      setRentals(freshRentals);
      
      setSuccess("Rental created successfully!");
      setOpenDialog(false);
      setRentalDialogStep(0);
      setConsentSubmitted(false);
      setConsentSubmittedAt('');
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create rental.");
    }
  };

  const handleNextToConsent = () => {
    if (!currentRider.userId || !currentRider.scooterId) {
      setError("Select both rider and scooter before continuing.");
      return;
    }
    if (!currentRider.rentalDays || currentRider.rentalDays < 1) {
      setError("Rental duration must be at least 1 day.");
      return;
    }
    setError('');
    setRentalDialogStep(1);
  };

  const handleSubmitConsentForRider = async () => {
    if (!currentRider.userId) {
      setError("Select rider first.");
      return;
    }
    try {
      setConsentSubmitting(true);
      const payload = {
        user_id: parseInt(currentRider.userId, 10),
        form_type: 'rental_agreement',
        form_data: {
          template: 'Consent-not-req-signature-HRP-5011-4',
          source: 'portal_rental_create',
          rider_id: parseInt(currentRider.userId, 10),
          scooter_id: parseInt(currentRider.scooterId, 10),
          rental_days: currentRider.rentalDays,
        }
      };
      const response = await api.post('/consent-forms', payload);
      const saved = response?.data?.data || {};
      setConsentSubmitted(true);
      setConsentSubmittedAt(saved.submitted_at || saved.created_at || new Date().toISOString());
      setSuccess('Consent form submitted for this rider.');
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit consent form.");
    } finally {
      setConsentSubmitting(false);
    }
  };

  const handleSendReminder = async (rental) => {
    try {
      // Call the new endpoint to send reminder for this rental
      const response = await api.post(`/rentals/${rental.rental_id}/send-reminder`);
      setSuccess(response.data.message || 'Reminder sent!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reminder');
    }
  };

  const handleEditRental = (rental) => {
    setEditRental({
      ...rental,
      end_time: rental.end_time ? rental.end_time.split('T')[0] : '',
      campus_location: (scooters.find(s => s.scooter_id === rental.scooter_id)?.campus_location) || '',
    });
    setOpenEditDialog(true);
  };

  const handleUpdateRental = async () => {
    try {
      let endTimeFormatted = editRental.end_time;
      if (endTimeFormatted && !endTimeFormatted.includes('T')) {
        // If only date is picked, add time part
        endTimeFormatted = endTimeFormatted + 'T00:00:00';
      }
      await updateRental(editRental.rental_id, {
        end_time: endTimeFormatted,
        status: editRental.status,
        scooter_id: editRental.scooter_id,
        // campus_location is updated via scooter update
      });
      // Update campus_location if changed
      const scooter = scooters.find(s => s.scooter_id === editRental.scooter_id);
      if (scooter && scooter.campus_location !== editRental.campus_location) {
        await api.put(`/scooters/${editRental.scooter_id}`, {
          campus_location: editRental.campus_location
        });
      }
      setOpenEditDialog(false);
      setEditRental(null);
      await fetchScooters();
      await fetchRentals();
    } catch (err) {
      setError('Failed to update rental. Create a new rental since status cannot be changed after return.');
    }
  };

  const handleDeleteRental = async (rentalId) => {
    if (!window.confirm('Are you sure you want to delete this rental?')) return;
    try {
      await deleteRental(rentalId);
      await fetchRentals();
    } catch (err) {
      setError('Failed to delete rental.');
    }
  };

  // Fetch scooters from backend
  const fetchScooters = async () => {
    try {
      const data = await getScooters();
      setScooters(data);
    } catch (err) {
      setError('Failed to load scooters');
    }
  };

  // Fetch rentals from backend
  const fetchRentals = async () => {
    try {
      const data = await getRentals();
      setRentals(data);
    } catch (err) {
      setError('Failed to load rentals');
    }
  };

  // Filtering, searching, sorting
  const filteredRentals = rentals.filter(rental => {
    const rider = riders.find(r => r.user_id === rental.user_id);
    let userDetails = {};
    try {
      userDetails = JSON.parse(rider?.user_details || '{}');
    } catch {}
    const name = userDetails.name || rider?.name || '';
    const email = rider?.email || '';
    const scooter = scooters.find(s => s.scooter_id === rental.scooter_id);
    const campus = scooter?.campus_location || '';
    const searchText = `${name} ${email} ${rental.scooter_id} ${campus}`.toLowerCase();
    const matchesSearch = searchText.includes(search.toLowerCase());
    const matchesStatus = !statusFilter || rental.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedRentals = [...filteredRentals].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    if (sortBy === 'start_time' || sortBy === 'end_time') {
      aVal = aVal ? new Date(aVal) : 0;
      bVal = bVal ? new Date(bVal) : 0;
    }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedRentals = sortedRentals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ pt:10,
          mt: 4, 
          mb: 6,
          flex: 1, // Takes up all available space
          pb: 8  }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: deepPurple[800] }}>
              Scooter Rentals
        </Typography>
        <Button
          variant="contained"
          startIcon={<HowToReg />}
          onClick={() => {
                setCurrentRider({
                  userId: "",
                  fullName: "",
                  email: "",
                  studentId: "",
                  scooterId: "",
                  rentalDays: 14
                });
            setRentalDialogStep(0);
            setConsentSubmitted(false);
            setConsentSubmittedAt('');
            setOpenDialog(true);
          }}
          sx={{
            bgcolor: deepPurple[600],
            '&:hover': { bgcolor: deepPurple[800] }
          }}
        >
          New Rental
        </Button>
      </Box>

          {/* Search, filter, sort controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'nowrap', alignItems: 'center' }}>
            <TextField
              label="Search"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              size="small"
              sx={{ mb: 0, flex: 1 }}
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 180, flex: '0 0 220px' }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {statusOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Snackbar open={!!error} autoHideDuration={2500} onClose={handleCloseError} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
            <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>{error}</Alert>
          </Snackbar>
          <Snackbar open={!!success} autoHideDuration={2500} onClose={handleCloseSuccess} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
            <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>{success}</Alert>
          </Snackbar>
          
          {reminders.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {reminders.length} rental{reminders.length > 1 ? 's' : ''} expiring soon. Reminders have been sent (reminders are sent only once, 3 days before expiration).
            </Alert>
          )}

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: deepPurple[50] }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Rider</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Scooter</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'start_time'}
                    direction={sortBy === 'start_time' ? sortOrder : 'asc'}
                    onClick={() => {
                      setSortBy('start_time');
                      setSortOrder(sortBy === 'start_time' && sortOrder === 'asc' ? 'desc' : 'asc');
                    }}
                  >
                    Rental Period
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Campus Location</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'status'}
                    direction={sortBy === 'status' ? sortOrder : 'asc'}
                    onClick={() => {
                      setSortBy('status');
                      setSortOrder(sortBy === 'status' && sortOrder === 'asc' ? 'desc' : 'asc');
                    }}
                  >
                    Rental Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {paginatedRentals.map((rental) => {
                  const rider = riders.find(r => r.user_id === rental.user_id);
                  let userDetails = {};
                  try {
                    userDetails = JSON.parse(rider?.user_details || "{}");
                  } catch (e) {
                    console.error("Error parsing user details:", e);
                  }
                  const scooter = scooters.find(s => s.scooter_id === rental.scooter_id);
                
                return (
                    <TableRow key={rental.rental_id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, bgcolor: purple[100], color: purple[800] }}>
                          <Person />
                        </Avatar>
                        <Box>
                            <Typography fontWeight="medium">{userDetails.name || "Unknown"}</Typography>
                          <Typography variant="body2" color="text.secondary">
                              {rider?.email || "No email"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<TwoWheeler />}
                          label={`${rental.scooter_id}`}
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                        <Typography>
                          Start: {new Date(rental.start_time).toLocaleString()}
                        </Typography>
                        <Typography>
                          End: {rental.end_time ? new Date(rental.end_time).toLocaleString() : "Not set"}
                        </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {scooter?.campus_location || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={statusOptions.find(opt => opt.value === rental.status)?.icon}
                        label={statusOptions.find(opt => opt.value === rental.status)?.label || rental.status}
                        sx={{
                          backgroundColor: `${statusOptions.find(opt => opt.value === rental.status)?.color || grey[300]}10`,
                          color: statusOptions.find(opt => opt.value === rental.status)?.color || grey[800],
                          fontWeight: 600,
                          border: `1px solid ${statusOptions.find(opt => opt.value === rental.status)?.color || grey[800]}30`,
                          px: 1,
                          '& .MuiChip-icon': {
                            color: statusOptions.find(opt => opt.value === rental.status)?.color || grey[800]
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {(isAdmin || isEmployee) && (
                          <Tooltip title="Edit">
                            <IconButton color="primary" onClick={() => handleEditRental(rental)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAdmin && (
                          <Tooltip title="Delete">
                            <IconButton color="error" onClick={() => handleDeleteRental(rental.rental_id)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Send Reminder">
                          <IconButton color="primary" onClick={() => handleSendReminder(rental)}>
                            <Email />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={sortedRentals.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      </Paper>
      </Container>

      <Dialog open={openDialog} onClose={() => {
        setOpenDialog(false);
        setRentalDialogStep(0);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>New Rental</DialogTitle>
        <DialogContent dividers>
          {rentalDialogStep === 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Select Rider"
                value={currentRider.userId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  setCurrentRider(prev => ({ ...prev, userId: selectedId }));
                  setConsentSubmitted(false);
                  setConsentSubmittedAt('');
                  handleSelectRider(selectedId);
                }}
              >
                {riders
                  .filter(rider => {
                    // Filter out riders who have a rental with status 'rented'
                    const hasRentedRental = rentals.some(
                      r => String(r.user_id) === String(rider.user_id) && r.status === 'rented'
                    );
                    return !hasRentedRental;
                  })
                  .map((u) => {
                    let userDetails = {};
                    try {
                      userDetails = JSON.parse(u.user_details || "{}");
                    } catch (e) {
                      console.error("Error parsing user details:", e);
                    }
                    const displayName = userDetails.name || u.name || "Unknown";
                    const studentId = userDetails.student_id || u.student_id || "No ID";
                    return (
                      <MenuItem key={`rider-${u.user_id}`} value={String(u.user_id)}>
                        {displayName} – {studentId}
                      </MenuItem>
                    );
                  })}
              </TextField>
            </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                label="Full Name"
                value={currentRider.fullName}
                disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                value={currentRider.email}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Student ID"
                value={currentRider.studentId}
                disabled
                  />
                </Grid>
                <Grid item xs={12}>
              <TextField
                select
                fullWidth
                      label="Scooter ID"
                value={currentRider.scooterId}
                onChange={(e) => {
                  setCurrentRider(prev => ({ ...prev, scooterId: e.target.value }));
                  setConsentSubmitted(false);
                  setConsentSubmittedAt('');
                }}
                    >
                      {scooters
                        .filter(s => s.status === 'available')
                  .map((s) => (
                    <MenuItem key={`scooter-${s.scooter_id}`} value={s.scooter_id}>
                      {`${s.scooter_id} (${s.status}) - ${s.serial || ''}`}
                    </MenuItem>
                  ))}
              </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Rental Duration (days)"
                    type="number"
                value={currentRider.rentalDays}
                onChange={(e) => {
                  setCurrentRider(prev => ({ ...prev, rentalDays: parseInt(e.target.value) || 14 }));
                  setConsentSubmitted(false);
                  setConsentSubmittedAt('');
                }}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
          </Grid>
          ) : (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Submit Consent Form</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review the consent PDF and submit consent before creating this rental.
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: deepPurple[50] }}>
                <Typography variant="body2"><strong>Rider:</strong> {currentRider.fullName || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {currentRider.email || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Student ID:</strong> {currentRider.studentId || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Scooter ID:</strong> {currentRider.scooterId || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Rental Duration:</strong> {currentRider.rentalDays} days</Typography>
              </Paper>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdf />}
                  onClick={() => window.open(`/api/consent-forms/pdf/${CONSENT_PDF_FILENAME}`, '_blank', 'noopener,noreferrer')}
                >
                  View Consent PDF
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitConsentForRider}
                  disabled={consentSubmitting || consentSubmitted}
                >
                  {consentSubmitting ? 'Submitting...' : consentSubmitted ? 'Consent Submitted' : 'Submit Consent Form'}
                </Button>
              </Stack>
              {consentSubmittedAt && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Consent submitted at {new Date(consentSubmittedAt).toLocaleString()}.
                </Alert>
              )}
              {!consentSubmitted && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Create Rental is enabled after consent is submitted.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false);
            setRentalDialogStep(0);
            setConsentSubmitted(false);
            setConsentSubmittedAt('');
          }}>Cancel</Button>
          {rentalDialogStep === 0 ? (
            <Button variant="contained" onClick={handleNextToConsent}>Next</Button>
          ) : (
            <>
              <Button onClick={() => setRentalDialogStep(0)}>Back</Button>
              <Button variant="contained" onClick={handleCreateRental} disabled={!consentSubmitted}>Create Rental</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Rental</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rider"
                value={(() => {
                  const rider = riders.find(r => r.user_id === editRental?.user_id);
                  let userDetails = {};
                  try {
                    userDetails = JSON.parse(rider?.user_details || '{}');
                  } catch {}
                  return userDetails.name || rider?.name || '';
                })()}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rental End Date"
                type="date"
                name="end_time"
                value={editRental?.end_time || ''}
                onChange={e => setEditRental({ ...editRental, end_time: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Scooter"
                name="scooter_id"
                value={editRental?.scooter_id || ''}
                onChange={e => setEditRental({ ...editRental, scooter_id: e.target.value })}
              >
                {scooters.map(s => (
                  <MenuItem key={s.scooter_id} value={s.scooter_id}>
                    {`${s.scooter_id} (${s.status}) - ${s.serial || ''}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campus Location"
                name="campus_location"
                value={editRental?.campus_location || ''}
                onChange={e => setEditRental({ ...editRental, campus_location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Rental Status"
                name="status"
                value={editRental?.status || ''}
                onChange={e => setEditRental({ ...editRental, status: e.target.value })}
              >
                {statusOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateRental}>Save Changes</Button>
        </DialogActions>
      </Dialog>
      
        <Footer />
    </Box>
  );
};

export default Rental;